import {
  ContentTemplate,
  ContentType,
  LayoutBlock
} from '@dadohr/shared/domainTypes/experience';
import shortid from 'shortid';

/**
 * Mock API that simulates a multi-agent content generation system
 *
 * In production, this would be replaced with:
 * 1. Content Planner Agent - Determines what blocks to create based on prompt
 * 2. Individual Content-Type Agents - Each generates specific content:
 *    - Heading Agent
 *    - Text Agent
 *    - Image Agent
 *    - Callout Agent
 *    - etc.
 *
 * This architecture allows:
 * - Independent scaling per content type
 * - Specialized models/prompts per content type
 * - Easy addition of new content types
 * - Package-level isolation (changes to one type don't affect others)
 */

/**
 * Simulates the Content Planner Agent
 * Analyzes the prompt and decides what blocks to create
 */
export const mockGenerateContentBlocks = async (
  prompt: string
): Promise<Array<ContentTemplate | LayoutBlock>> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1200));

  // Simple pattern matching to determine block structure
  // In production, this would be a sophisticated LLM call
  const lowerPrompt = prompt.toLowerCase();

  // Return a structured set of blocks
  // Each block would be generated by its specialized agent
  const blocks: Array<ContentTemplate | LayoutBlock> = [];

  // Heading Agent - Creates section headers
  blocks.push({
    id: shortid(),
    parentLayoutElement: null,
    type: ContentType.HEADING,
    data: {
      t: getHeadingForPrompt(lowerPrompt)
    }
  });

  // Text Agent - Creates introductory content
  blocks.push({
    id: shortid(),
    parentLayoutElement: null,
    type: ContentType.TEXT,
    data: {
      t: {
        t: getIntroTextForPrompt(lowerPrompt),
        v: 1
      }
    }
  });

  // Image Agent - Creates visual content
  // Uses placehold.co for placeholder images
  blocks.push({
    id: shortid(),
    parentLayoutElement: null,
    type: ContentType.IMAGE,
    data: {
      src: getImageUrlForPrompt(lowerPrompt),
      alt: 'Generated image content',
      align: 'center',
      width: 100,
      type: 'url'
    }
  });

  // Callout Agent - Creates emphasized content
  blocks.push({
    id: shortid(),
    parentLayoutElement: null,
    type: ContentType.CALLOUT,
    data: {
      color: getCalloutColorForPrompt(lowerPrompt),
      t: {
        t: getCalloutTextForPrompt(lowerPrompt),
        v: 1
      }
    }
  });

  // Text Agent - Additional detailed content
  blocks.push({
    id: shortid(),
    parentLayoutElement: null,
    type: ContentType.TEXT,
    data: {
      t: {
        t: getDetailTextForPrompt(lowerPrompt),
        v: 1
      }
    }
  });

  // Divider Agent - Creates visual separation
  blocks.push({
    id: shortid(),
    parentLayoutElement: null,
    type: ContentType.DIVIDER,
    data: {
      variant: 'light'
    }
  });

  return blocks;
};

// Helper functions that simulate specialized agent outputs
function getHeadingForPrompt(prompt: string): string {
  if (prompt.includes('welcome')) return 'Welcome to the Team! 👋';
  if (prompt.includes('onboard')) return 'Your Onboarding Journey';
  if (prompt.includes('policy')) return 'Important Policies';
  if (prompt.includes('benefit')) return 'Your Benefits Package';
  if (prompt.includes('training')) return 'Training & Development';
  return 'Getting Started';
}

function getIntroTextForPrompt(prompt: string): string {
  if (prompt.includes('welcome')) {
    return "We're thrilled to have you join our team! This guide will help you navigate your first days and set you up for success. Take your time to review everything, and don't hesitate to reach out if you have questions.";
  }
  if (prompt.includes('onboard')) {
    return "Your onboarding experience is designed to help you transition smoothly into your new role. Over the next few weeks, you'll meet your team, learn our processes, and get hands-on with your responsibilities.";
  }
  if (prompt.includes('policy')) {
    return 'Understanding our company policies is essential for maintaining a positive and productive work environment. Please review these guidelines carefully and keep them in mind as you begin your journey with us.';
  }
  if (prompt.includes('benefit')) {
    return "We offer a comprehensive benefits package designed to support your health, well-being, and financial security. Below you'll find an overview of the key benefits available to you.";
  }
  return 'This content has been generated to help you get started. Each section below provides important information tailored to your needs.';
}

function getImageUrlForPrompt(prompt: string): string {
  if (prompt.includes('welcome')) {
    return 'https://placehold.co/600x400/4A90E2/ffffff?text=Welcome+Aboard';
  }
  if (prompt.includes('onboard')) {
    return 'https://placehold.co/600x400/50E3C2/ffffff?text=Onboarding+Journey';
  }
  if (prompt.includes('policy')) {
    return 'https://placehold.co/600x400/F5A623/ffffff?text=Company+Policies';
  }
  if (prompt.includes('benefit')) {
    return 'https://placehold.co/600x400/7ED321/ffffff?text=Benefits+Overview';
  }
  if (prompt.includes('training')) {
    return 'https://placehold.co/600x400/BD10E0/ffffff?text=Training+Programs';
  }
  return 'https://placehold.co/600x400/9013FE/ffffff?text=AI+Generated';
}

function getCalloutColorForPrompt(
  prompt: string
): 'blue' | 'green' | 'yellow' | 'red' | 'purple' {
  if (prompt.includes('important') || prompt.includes('critical')) return 'red';
  if (prompt.includes('tip') || prompt.includes('help')) return 'blue';
  if (prompt.includes('success') || prompt.includes('complete')) return 'green';
  if (prompt.includes('warning') || prompt.includes('note')) return 'yellow';
  return 'purple';
}

function getCalloutTextForPrompt(prompt: string): string {
  if (prompt.includes('welcome')) {
    return '**Pro Tip:** Your manager will schedule a 1-on-1 meeting during your first week. Come prepared with questions about your role and team dynamics!';
  }
  if (prompt.includes('onboard')) {
    return '**Important:** Make sure to complete all assigned tasks in your onboarding checklist within the first 30 days. This ensures you have access to all necessary systems and resources.';
  }
  if (prompt.includes('policy')) {
    return '**Please Note:** All employees are required to acknowledge they have read and understood these policies. You can find the acknowledgment form in your HR portal.';
  }
  if (prompt.includes('benefit')) {
    return '**Action Required:** You have 30 days from your start date to enroll in benefits. Contact HR if you need assistance choosing the right options for you and your family.';
  }
  return '**Remember:** Each content type is handled by its own specialized AI agent in the production system, allowing for sophisticated and contextual content generation.';
}

function getDetailTextForPrompt(prompt: string): string {
  if (prompt.includes('welcome')) {
    return "During your first week, you'll receive access to all necessary tools and platforms. Your team lead will guide you through our workflows and introduce you to key stakeholders. We encourage you to ask questions and take notes – there's a lot to learn, and we're here to support you every step of the way!";
  }
  if (prompt.includes('onboard')) {
    return 'Your onboarding schedule includes team introductions, system training, and role-specific workshops. Each session is designed to build on the previous one, giving you a solid foundation for success. Check your calendar for upcoming sessions and mark any conflicts with your manager.';
  }
  if (prompt.includes('policy')) {
    return 'Our policies cover workplace conduct, confidentiality, data security, and more. They apply to all employees and are updated annually. If you have questions about any policy, please reach out to your HR representative for clarification.';
  }
  if (prompt.includes('benefit')) {
    return 'Benefits include health insurance, retirement plans, paid time off, professional development stipends, and wellness programs. Detailed information about each benefit, including eligibility and enrollment procedures, is available in your employee handbook.';
  }
  return 'This detailed content provides additional context and information. In production, the AI would analyze your specific needs and generate highly relevant, personalized content for your audience.';
}

/**
 * Future production architecture would include:
 *
 * Core AI Package (@dado/ai-core):
 * - LLM client abstractions (OpenAI, Anthropic, etc.)
 * - Prompt management
 * - Rate limiting
 * - Error handling
 * - Monitoring/logging
 *
 * Content AI Package (@dado/ai-content):
 * - Content Planner Agent
 * - Shared utilities for content generation
 * - Cross-content-type coordination
 *
 * Per-Content-Type Packages:
 * - @dado/ai-content-heading
 * - @dado/ai-content-text
 * - @dado/ai-content-image
 * - @dado/ai-content-callout
 * - @dado/ai-content-divider
 * - etc.
 *
 * Benefits:
 * - Changes to heading generation don't affect text generation
 * - Can use different models per content type
 * - Can version agents independently
 * - Easy to add new content types
 * - Clear ownership and testing boundaries
 */
